import * as React from 'react'
import clsx from 'clsx'
import {
  $createParagraphNode,
  $getRoot,
  $insertNodes,
  CLEAR_EDITOR_COMMAND,
  LexicalEditor,
  TextNode
} from 'lexical'
import { $generateNodesFromDOM } from '@lexical/html'
import theme from './themes/Theme'
import ToolbarPlugin from './plugins/ToolbarPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin'
import { HashtagNode } from '@lexical/hashtag'
//import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import { ListItemNode, ListNode } from '@lexical/list'
import { CodeHighlightNode, CodeNode } from '@lexical/code'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import { OverflowNode } from '@lexical/overflow'
import { TablePlugin as NewTablePlugin } from './plugins/TablePlugin'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import {
  ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  CHECK_LIST,
  $convertFromMarkdownString
} from '@lexical/markdown'
import { $generateHtmlFromNodes } from '@lexical/html'
import { MaxLengthPlugin } from './plugins/MaxLengthPlugin'
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin'
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin'
import AutoLinkPlugin from './plugins/AutoLinkPlugin'
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin'
import TabFocusPlugin from './plugins/TabFocusPlugin'
import SpeechToTextPlugin from './plugins/SpeechToTextPlugin'
import ActionsPlugin from './plugins/ActionsPlugin'
import EditorBlurPlugin from './plugins/BlurPugin'
import CollapsiblePlugin from './plugins/CollapsiblePlugin'
import ImagesPlugin from './plugins/ImagesPlugin'
import LexicalClickableLinkPlugin from '@lexical/react/LexicalClickableLinkPlugin'
import DragDropPaste from './plugins/DragDropPastePlugin'
import TableCellResizer from './plugins/TableCellResizer'
import TableCellActionMenuPlugin from './plugins/TableActionMenuPlugin'
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ExtendedTextNode } from './nodes/ExtendedText'
import { CollapsibleContainerNode } from './plugins/CollapsiblePlugin/CollapsibleContainerNode'
import { CollapsibleContentNode } from './plugins/CollapsiblePlugin/CollapsibleContentNode'
import { CollapsibleTitleNode } from './plugins/CollapsiblePlugin/CollapsibleTitleNode'
import { ImageNode } from './nodes/ImageNode'
import YouTubePlugin from './plugins/YouTubePlugin'
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin'
import { YouTubeNode } from './nodes/YouTubeNode'
import { CAN_USE_DOM } from './utils/canUseDOM'
import { clearClosedCollapsibles } from './utils/clearClosedCollapsibles'
import useStyles from './styles'

interface EditorProps {
  value: string
  maxLength?: number
  autoFocus?: boolean
  enabled?: boolean
  hasMarkdown?: boolean
  placeholder?: string
  showToolbar?: boolean
  disableImages?: boolean
  showSpeechToText?: boolean
  enableDrag?: boolean
  setIsEditorEmpty?: (val: boolean) => void
  onChange: (val: any) => void
  onBlur?: () => void
  className?: string
}

type WrappedEditorProps = EditorProps

const EMPTY_NOTE =
  '<head></head><body><p class="editor-paragraph"><br></p></body>'
const EMPTY_WITH_TWO_PARAGRAPHS =
  '<p class="editor-paragraph"><p class="editor-paragraph"><br></p></p>'
export const EMPTY_SINGLE_PARAGRAPH = '<p class="editor-paragraph"><br></p>'

//legacy descriptions have nextlines, replace themn with br.
export function replaceWithBr (text) {
  const result = text.replace(/\n/g, '<br />')
  if (isEmpty(result)) return ''
  return result
}

//temporary horrible workaround for empty value
export const isEmpty = value =>
  [EMPTY_WITH_TWO_PARAGRAPHS, EMPTY_SINGLE_PARAGRAPH, EMPTY_NOTE].includes(
    value
  )

function Placeholder ({ placeholder }: { placeholder: string }) {
  return <div className='editor-placeholder'>{placeholder}</div>
}

const WrappedEditor: React.FC<WrappedEditorProps> = ({
  onChange,
  onBlur,
  value,
  autoFocus = false,
  maxLength = 10000,
  enabled = true,
  showToolbar = true,
  showSpeechToText = true,
  disableImages = false,
  enableDrag = false,
  placeholder = 'Type something...',
  className
}) => {
  const [editor] = useLexicalComposerContext()
  const classes = useStyles()
  const [floatingAnchorElem, setFloatingAnchorElem] =
    React.useState<HTMLDivElement | null>(null)
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    React.useState<boolean>(false)

  function handleOnChange (editorState, editor) {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor)

      onChange(clearClosedCollapsibles(html))
    })
  }

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem)
    }
  }

  const cellEditorConfig = {
    namespace: 'Playground',
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      HashtagNode,
      CodeHighlightNode,
      AutoLinkNode,
      LinkNode,
      ImageNode
    ],
    onError: (error: Error) => {
      throw error
    },
    theme: theme
  }

  //there is a weird scenario with existing data where even if we clear everything the editor has an extra <p> and breaks the formatter, everything. this is a ""temp"" (probably forever, lol)
  React.useEffect(() => {
    if (isEmpty(value)) {
      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined)
    }
  }, [value, editor])

  React.useEffect(() => {
    editor.setEditable(enabled)
  }, [editor, enabled])

  //When a text is set, it automatically assigns focus, regardless the plugin or not, this will prevent it
  //https://github.com/facebook/lexical/issues/4474#issuecomment-1558485693
  React.useEffect(() => {
    // Set the editor to not editable - it won't take focus while in that state
    editor.setEditable(false)

    const unregister = editor.registerNodeTransform(TextNode, textNode => {})

    // Set the editor back to editable a frame later
    requestAnimationFrame(() => editor.setEditable(true))

    return unregister
  }, [editor])

  React.useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport)
      }
    }
    updateViewPortWidth()
    window.addEventListener('resize', updateViewPortWidth)

    return () => {
      window.removeEventListener('resize', updateViewPortWidth)
    }
  }, [isSmallWidthViewport])

  return (
    <div className={clsx('editor-container', classes.root, className)}>
      {enabled && (
        <ToolbarPlugin
          showToolbar={showToolbar}
          anchorElem={floatingAnchorElem}
          disableImages={disableImages}
          isSmallWidthViewport={isSmallWidthViewport}
        />
      )}
      <div className='editor-inner'>
        <RichTextPlugin
          contentEditable={
            <div className='editor' ref={onRef}>
              <ContentEditable className='editor-input' />
            </div>
          }
          placeholder={<Placeholder placeholder={placeholder} />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={handleOnChange} />
        {!disableImages && <DragDropPaste />}
        <HistoryPlugin />
        {autoFocus && <AutoFocusPlugin />}
        <ClearEditorPlugin />
        <YouTubePlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={3} />
        <TabFocusPlugin />
        <TabIndentationPlugin />
        <LinkPlugin />
        <AutoLinkPlugin />
        <AutoEmbedPlugin />
        {!disableImages && <ImagesPlugin captionsEnabled={false} />}
        <SpeechToTextPlugin />
        {!enabled && <LexicalClickableLinkPlugin />}
        <EditorBlurPlugin onBlur={onBlur} />
        <CollapsiblePlugin />
        <MarkdownShortcutPlugin
          transformers={[
            CHECK_LIST,
            ...ELEMENT_TRANSFORMERS,
            ...TEXT_FORMAT_TRANSFORMERS,
            ...TEXT_MATCH_TRANSFORMERS
          ]}
        />
        {showSpeechToText && <ActionsPlugin />}
        <MaxLengthPlugin maxLength={maxLength} />
        {/* <CharacterLimitPlugin charset={'UTF-16'} maxLength={10000} /> */}
        <CodeHighlightPlugin />
        {floatingAnchorElem && enabled && !isSmallWidthViewport && (
          <>
            {enableDrag && (
              <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
            )}
            <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem} />
            <TableCellActionMenuPlugin
              anchorElem={floatingAnchorElem}
              cellMerge={true}
            />
          </>
        )}
        <TablePlugin hasCellMerge hasCellBackgroundColor />
        <TableCellResizer />
        <NewTablePlugin cellEditorConfig={cellEditorConfig}>
          <AutoFocusPlugin />
          <RichTextPlugin
            contentEditable={
              <ContentEditable className='TableNode__contentEditable' />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ImagesPlugin captionsEnabled={false} />
          <LinkPlugin />
          <LexicalClickableLinkPlugin />
          <FloatingTextFormatToolbarPlugin />
        </NewTablePlugin>
      </div>
    </div>
  )
}

const Editor: React.FC<EditorProps> = props => {
  const { value = '', hasMarkdown = false } = props

  const initEditor = (editor: LexicalEditor) => {
    if (!hasMarkdown) {
      // In the browser you can use the native DOMParser API to parse the HTML string.
      const parser = new DOMParser()
      const dom = parser.parseFromString(value, 'text/html')

      // Once you have the DOM instance it's easy to generate LexicalNodes.
      const nodes = $generateNodesFromDOM(editor, dom)
        .map(node => {
          if (node.getType() === 'text' || node.getType() === 'extended-text') {
            if (node.getTextContent().trim() === '') {
              return null
            } else {
              return $createParagraphNode().append(node)
            }
          }

          if (node.getType() === 'linebreak') {
            return null
          }

          return node
        })
        .filter(node => !!node)

      // Select the root
      $getRoot().select()

      // Insert them at a selection.
      $insertNodes(nodes)
    } else {
      return $convertFromMarkdownString(value, [
        CHECK_LIST,
        ...ELEMENT_TRANSFORMERS,
        ...TEXT_FORMAT_TRANSFORMERS,
        ...TEXT_MATCH_TRANSFORMERS
      ])
    }
  }

  const editorConfig = {
    theme: theme,
    editorState: value === '' ? undefined : initEditor,
    namespace: 'sonarcx',
    // Handling of errors during update
    onError (error) {
      console.error('Error during lexical update!!!', error)
    },
    // Any custom nodes go here
    nodes: [
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text, node.__key)
      },
      HeadingNode,
      ListNode,
      HashtagNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      OverflowNode,
      CollapsibleContainerNode,
      CollapsibleContentNode,
      CollapsibleTitleNode,
      ImageNode,
      YouTubeNode
    ]
  }

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <WrappedEditor {...props} />
    </LexicalComposer>
  )
}

export default Editor
